import { Country, Locale } from 'App/i18n/enums';
import { ReportDateRangeType } from 'commons/types';
import { config } from 'config';
import moment from 'moment';
import { IStoreInfo } from 'redux/slices/types';

export const getIntlLocaleForStore = (
  storeInfo: Pick<IStoreInfo, 'country' | 'language'>,
): Locale => {
  const language = (storeInfo.language || '').toLowerCase();

  if (language === 'br' || language.startsWith('pt')) {
    return Locale.BRAZIL;
  }

  switch (storeInfo.country) {
    case Country.ARGENTINA:
      return Locale.ARGENTINA;
    case Country.MEXICO:
      return Locale.MEXICO;
    case Country.BRAZIL:
      return Locale.BRAZIL;
    default:
      return Locale.ARGENTINA;
  }
};

export const dateHasPassed = (dateString: string | Date): boolean => {
  const today = new Date();
  const date = new Date(dateString);
  return date < today;
};

export const daysPassedSince = (dateString: string): number => {
  const today = new Date();
  const date = new Date(dateString);
  const difference = today.getTime() - date.getTime();
  const daysPassed = Math.floor(difference / (1000 * 60 * 60 * 24));
  return daysPassed;
};

export const daysUntil = (dateString: Date): number => {
  const today = new Date();
  const date = dateString;
  const difference = date.getTime() - today.getTime();
  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  return days;
};

export const earliestDate = (dates: Date[]): Date => {
  return new Date(Math.min(...dates.map((date) => date.getTime())));
};

export const getRelativeDateFromToday = (days = 0): string => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

export const todayDate = (): string => {
  return getRelativeDateFromToday(0);
};

export const formatLongDateWithoutYearOffset = (
  storeInfo: IStoreInfo,
  date: string,
): string => {
  // Date() converts from UTC to users timezone but 'date' comes without timezone
  // so to fix "off by 1 day" bug we add the timezone offset
  const dateFormatted = new Date(date);
  dateFormatted.setMinutes(
    dateFormatted.getMinutes() + dateFormatted.getTimezoneOffset(),
  );

  return formatLongDateWithoutYear(storeInfo, dateFormatted);
};

export const formatLongDateWithoutYear = (
  storeInfo: IStoreInfo,
  date: string | Date,
): string => {
  const dateFormatted = new Date(date);
  const locale = `${storeInfo.language}-${storeInfo.country}`;

  return dateFormatted.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
  });
};

export const formatLongDateTime = (
  storeInfo: IStoreInfo,
  date: string,
): string => {
  const locale = `${storeInfo.language}-${storeInfo.country}`;
  return new Date(date)
    .toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    .replace(',', ' -');
};

export const formatDate = (storeInfo: IStoreInfo, date: string): string => {
  const dateFormated = moment(date).locale(storeInfo.language).format('D MMM');
  return dateFormated.replace('.', '');
};

export const formatLongDate = (storeInfo: IStoreInfo, date: string): string => {
  const locale = `${storeInfo.language}-${storeInfo.country}`;
  return new Date(date)
    .toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    .replace('-', ' ');
};

export const formatNumericDate = (
  storeInfo: IStoreInfo,
  date: string,
  sep: string = '/',
): string => {
  const locale = `${storeInfo.language}-${storeInfo.country}`;
  return new Date(date)
    .toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    .replaceAll('/', sep);
};

export const formatNumericDayMonth = (
  date: string | Date,
  sep: string = '/',
): string => {
  const newDate = new Date(date);
  const day = String(newDate.getDate()).padStart(2, '0');
  const month = String(newDate.getMonth() + 1).padStart(2, '0');
  return `${day}${sep}${month}`;
};

export const formatTime = (
  date: string,
  use24HourFormat: boolean = false,
): string => {
  return new Date(date).toLocaleTimeString('default', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: use24HourFormat ? 'h23' : 'h12',
  });
};

export const formatDateTime = (
  dateParam: string | Date | null | undefined,
  storeInfo: IStoreInfo,
): string => {
  if (!dateParam) return '-';
  const date = new Date(dateParam);
  if (isNaN(date.getTime())) return '-';

  const locale = getIntlLocaleForStore(storeInfo);

  const dayMonthYear = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);

  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);

  const timeConnector = locale === Locale.BRAZIL ? 'às' : 'a las';
  return `${dayMonthYear} ${timeConnector} ${time}`;
};

export const formatPaymentDate = (
  dateParam: string | Date,
  padding: number,
  locale: 'es-AR' | 'es-MX' | 'pt-BR',
) => {
  const date = new Date(dateParam);
  date.setDate(date.getDate() + padding);

  const dayMonth = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
  }).format(date);

  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);

  const timeConnector = locale === 'pt-BR' ? 'às' : 'a las';
  return `${dayMonth} ${timeConnector} ${time}`;
};

export const formatFundsAvailableDate = (
  dateParam: string | Date,
  locale: 'es-AR' | 'es-MX' | 'pt-BR',
): string => {
  const date = new Date(dateParam);
  const dayMonth = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
  }).format(date);
  const isMidnight = date.getHours() === 0 && date.getMinutes() === 0;
  if (isMidnight) return dayMonth;
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
  const timeConnector = locale === 'pt-BR' ? 'às' : 'a las';
  return `${dayMonth} ${timeConnector} ${time}`;
};

export const formatSpanishLongDate = (date: Date, withHour = true): string => {
  const newDate = new Date(date);
  const day = newDate.getDate();
  const month = new Intl.DateTimeFormat('es-AR', { month: 'long' }).format(
    newDate,
  );
  const hour = `${newDate.getHours()}`.padStart(2, '0');
  const minutes = `${newDate.getMinutes()}`.padStart(2, '0');

  return withHour
    ? `${day} de ${month} a las ${hour}:${minutes}`
    : `${day} de ${month}`;
};

export const reportRangeToDates = (
  dateRange: ReportDateRangeType,
  fromDate?: string,
  toDate?: string,
): {
  fromDate: string;
  toDate: string;
} => {
  const minimumDate = moment(config.reports.minReportDay);
  let newFromDate = minimumDate;
  let newToDate = moment();

  switch (dateRange) {
    case 'all':
      break;
    case 'last-24-hours':
      newFromDate = moment().subtract(1, 'days');
      break;
    case 'last-7-days':
      newFromDate = moment().subtract(7, 'days');
      break;
    case 'last-month':
      newFromDate = moment().subtract(1, 'months').startOf('month');
      newToDate = moment().subtract(1, 'months').endOf('month');
      break;
    case 'custom':
      newFromDate = moment(fromDate).startOf('day');
      newToDate = moment(toDate).endOf('day');
      break;
  }

  if (newFromDate.isBefore(minimumDate)) newFromDate = minimumDate;
  if (newToDate.isBefore(minimumDate)) newToDate = minimumDate;

  return {
    fromDate: newFromDate.format(),
    toDate: newToDate.format(),
  };
};
